import { logError } from "@/lib/client/errors";
import { footwearJpToEu } from "@/lib/shared/size";

export const itemSlug = (item: SearchResultItem) =>
  item.title
    .toLowerCase()
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 50) || "item";

export const brandSlug = (brand: BrandFilter) =>
  encodeURIComponent(brand.label.toLowerCase().replace(/\s/g, "_"));

export const getDomain = (url: string): string => {
  if (!url || url.includes("/goto/") || url.includes("plans")) {
    return url || "";
  }

  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.protocol}//${parsedUrl.host}`;
  } catch (error) {
    logError(error as Error, { url });
    return url;
  }
};

export const shorterCountryName = (region: string) => {
  switch (region) {
    case "United Arab Emirates":
      return "UAE";
    case "Hong Kong SAR China":
      return "Hong Kong";
    default:
      return region;
  }
};

export function generateVendorSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function findVendorBySlug(vendors: Vendor[], slug: string): Vendor | undefined {
  return vendors.find((vendor) => generateVendorSlug(vendor.label) === slug);
}

export function generateBrandSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function findBrandBySlug(brands: BrandFilter[], slug: string): BrandFilter | undefined {
  return brands.find((brand) => generateBrandSlug(brand.label) === slug);
}

/**
 * Format a number as a currency string (USD by default)
 */
export function formatPrice(price: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat("en-US").format(num);
}

/**
 * Format a count for display (e.g., 1200 -> "1.2k+")
 */
export function formatCount(num: number): string {
  if (!num) return "";
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k+`;
  return `${num}+`;
}

/**
 * Format a size string for display.
 * For footwear items, converts JP cm sizes (22–32) to EU sizes using the same
 * mapping as the size filter dropdown.
 */
export function formatSize(
  size: string | null | undefined,
  itemtype: string | null | undefined
): string | null {
  if (!size) return null;
  if (itemtype?.toLowerCase() !== "footwear") return size;
  const trimmed = size.trim();
  return footwearJpToEu[trimmed] ?? size;
}
